import type { ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface RadioTileOption {
  /** Stable value used to identify the option. */
  key: string;
  /** Primary label shown in bold. */
  label: ReactNode;
  /** Optional sub-label / helper below the primary label. */
  sub?: ReactNode;
  /** Optional right-aligned aside (e.g. price or badge). */
  aside?: ReactNode;
}

export interface RadioTilesProps {
  /** Radio-group `name`. */
  name: string;
  /** Currently-selected option key. */
  value: string;
  /** Called when the user picks a different option. */
  onChange: (key: string) => void;
  /** Options. */
  options: RadioTileOption[];
  /** Grid columns at md+ breakpoint. Default 1 (stacked). */
  columns?: 1 | 2 | 3 | 4;
  /** Optional aria-label for the fieldset. */
  ariaLabel?: string;
  className?: string;
}

/**
 * `<RadioTiles>` — large-target radio group as clickable tiles.
 *
 * Consolidates the two hand-rolled radio-tile blocks in RegisterInstitution
 * (plan picker + institution-kind picker), plus any future signup or
 * onboarding step that needs a "pick one of these" list where each option
 * has more than just a label.
 *
 * Uses the `.radio-tile` class from `kollez.css` — all colours, borders,
 * shadows, and focus rings come from tokens. No inline hex; no inline
 * `borderColor`/`background` per-tile.
 *
 * Semantics: the wrapping `<fieldset>` groups the radios; the visible
 * label is on each `<label>` (which contains its `<input type="radio">`).
 * The `data-selected` attribute drives the CSS accent state.
 */
export function RadioTiles({
  name,
  value,
  onChange,
  options,
  columns = 1,
  ariaLabel,
  className,
}: RadioTilesProps) {
  const colClass =
    columns === 4 ? 'md:grid-cols-4' :
    columns === 3 ? 'md:grid-cols-3' :
    columns === 2 ? 'md:grid-cols-2' :
                     '';
  return (
    <fieldset
      className={cn('grid gap-2 border-0 p-0 m-0', colClass, className)}
      aria-label={ariaLabel}
    >
      {options.map((o) => {
        const selected = o.key === value;
        return (
          <label
            key={o.key}
            className="radio-tile"
            data-selected={selected}
            style={{ position: 'relative' }}
          >
            <input
              type="radio"
              name={name}
              value={o.key}
              checked={selected}
              onChange={() => onChange(o.key)}
            />
            {o.aside ? (
              <span className="flex justify-between gap-3 flex-wrap items-baseline">
                <b>{o.label}</b>
                <span>{o.aside}</span>
              </span>
            ) : (
              <b>{o.label}</b>
            )}
            {o.sub && <span className="helper">{o.sub}</span>}
          </label>
        );
      })}
    </fieldset>
  );
}
